// v2/hooks/useCotizacion.js
//
// Hook para el flujo cotizar → reservar → iniciar pago.
//
// Mantiene el `hash` de la cotización vigente y maneja la expiración
// automática (los hashes expiran a 10 min).

import { useState, useCallback, useRef, useEffect } from 'react';
import InscripcionV2 from '../service/inscripcion.service.js';
import { codigoError, mensajeDeError } from '../service/client.js';

export function useCotizacion() {
  const [cotizando, setCotizando] = useState(false);
  const [cotizacion, setCotizacion] = useState(null); // { hash, total, items, expiraEn }
  const [error, setError] = useState(null);
  const expiraTimer = useRef(null);

  // Limpiar la cotización automáticamente al expirar (UI feedback).
  useEffect(() => {
    if (!cotizacion?.expiraEn) return undefined;
    const ms = new Date(cotizacion.expiraEn).getTime() - Date.now();
    if (ms <= 0) {
      setCotizacion(null);
      return undefined;
    }
    if (expiraTimer.current) clearTimeout(expiraTimer.current);
    expiraTimer.current = setTimeout(() => {
      setCotizacion(prev => prev?.hash === cotizacion.hash ? null : prev);
      setError({ code: 'COT_EXPIRADA', mensaje: 'Tu cotización expiró, vuelve a confirmar.' });
    }, ms);
    return () => clearTimeout(expiraTimer.current);
  }, [cotizacion]);

  const cotizar = useCallback(async ({ items, datosCliente, carritoId }) => {
    setCotizando(true);
    setError(null);
    try {
      const out = await InscripcionV2.cotizar({ items, datosCliente, carritoId });
      setCotizacion(out);
      return out;
    } catch (err) {
      const code = codigoError(err);
      setError({ code, mensaje: mensajeDeError(err) });
      throw err;
    } finally {
      setCotizando(false);
    }
  }, []);

  const reservar = useCallback(async () => {
    if (!cotizacion?.hash) {
      throw Object.assign(new Error('No hay cotización vigente'), { code: 'COT_NO_EXISTE' });
    }
    try {
      const out = await InscripcionV2.reservar(cotizacion.hash);
      return out;
    } catch (err) {
      const code = codigoError(err);
      setError({ code, mensaje: mensajeDeError(err) });
      throw err;
    }
  }, [cotizacion]);

  const iniciarPago = useCallback(async ({ returnUrl, datosCliente }) => {
    if (!cotizacion?.hash) {
      throw Object.assign(new Error('No hay cotización vigente'), { code: 'COT_NO_EXISTE' });
    }
    try {
      const out = await InscripcionV2.iniciarPago({
        hash: cotizacion.hash,
        returnUrl,
        datosCliente
      });
      return out;
    } catch (err) {
      const code = codigoError(err);
      setError({ code, mensaje: mensajeDeError(err) });
      throw err;
    }
  }, [cotizacion]);

  const reset = useCallback(() => {
    setCotizacion(null);
    setError(null);
    if (expiraTimer.current) {
      clearTimeout(expiraTimer.current);
      expiraTimer.current = null;
    }
  }, []);

  return {
    cotizando,
    cotizacion,
    error,
    cotizar,
    reservar,
    iniciarPago,
    reset
  };
}

export default useCotizacion;
